import { Pin } from 'lucide-react';
import { ASSIST_METRICS, ASSIST_TIME_BUCKETS, ASSIST_VIZ_KINDS, type AssistChartPlan, type AssistVizKind } from '@/shared/assist/AssistContract';
import type { AnalyticsField, AnalyticsFilter, AnalyticsMetric, TimeBucket } from '@/shared/analytics/AnalyticsTypes';
import type { TopicDefinition } from './TopicCatalogTypes';
import { assistFieldLabel, metricLabel } from '@/shared/analytics/MetricFormatting';

/**
 * The serializable configuration of a user-created visualization tile. It is
 * structurally the body of an {@link AssistChartPlan} (minus the `workflow`
 * discriminator and the model-authored `narrative`) so the AMA "pin" action and
 * the "Add insight" modal produce the exact same shape — one canonical spec, two
 * entry points. `filters` are baked into the tile and composed with the page's
 * live ambient filters at render time, mirroring how the assist chart query
 * merges them (see SurfaceAssistViewModel.runChart).
 */
export interface TileSpec {
    title: string;
    viz: AssistVizKind;
    /** The group-by field for `ranked-bars`/`donut`; unused for `trend`. */
    primaryField: AnalyticsField;
    metric: AnalyticsMetric;
    /** Bucket size for `trend`; unused otherwise. */
    bucket?: TimeBucket;
    /** Max rows for `ranked-bars`/`donut` (top-N); unused for `trend`. */
    limit?: number;
    /** Filters captured with the tile, composed with the page's live filters. */
    filters?: AnalyticsFilter[];
}

export type TileSource = 'ama' | 'add-insight';

/** A persisted custom tile: a {@link TileSpec} plus identity and provenance. */
export interface PinnedTile {
    id: string;
    surfaceId: string;
    spec: TileSpec;
    source: TileSource;
    /** ISO timestamp of when the tile was pinned. */
    savedAt: string;
}

/** Lifts an assist chart plan into a tile spec (the spec the chart already carries). */
const TILE_TITLE_MAX_LENGTH = 60;

/** Reduces a model-authored title to a label-sized one: first sentence only, hard-capped — models sometimes pack a whole narrative in. */
function tileTitle(raw: string): string {
    const firstSentence = raw.trim().split(/(?<=[.!?])\s+/)[0] ?? '';
    const clean = firstSentence.replace(/[.!?]+$/, '').trim();
    if (clean.length === 0) return 'Untitled insight';
    return clean.length > TILE_TITLE_MAX_LENGTH ? `${clean.slice(0, TILE_TITLE_MAX_LENGTH - 1).trimEnd()}…` : clean;
}

export function tileSpecFromChartPlan(plan: AssistChartPlan): TileSpec {
    return {
        title: tileTitle(plan.title),
        viz: plan.viz,
        primaryField: plan.primaryField,
        metric: plan.metric,
        bucket: plan.bucket,
        filters: plan.filters,
    };
}

/** Synthesizes a minimal insight definition from a pinned tile so its data points can drill into the shared L1/L2 pages. */
export function definitionFromTile(tile: PinnedTile): TopicDefinition {
    const spec = tile.spec;
    const lens = assistFieldLabel(spec.primaryField).toLowerCase();
    return {
        id: tile.id,
        title: spec.title,
        hook: '',
        question: '',
        icon: Pin,
        accent: 'indigo',
        vizKind: spec.viz,
        primaryField: spec.primaryField,
        headlineMetric: spec.metric,
        unitLabel: metricLabel(spec.metric).toLowerCase(),
        lensLabel: lens,
        lensPlural: `${lens}s`,
        timeBucket: spec.bucket,
    };
}

/**
 * A stable identity for a tile spec's query shape (viz / field / metric / bucket
 * + its own filters), used to detect whether an equivalent tile is already
 * pinned. `primaryField` is omitted for `trend`, where it is meaningless.
 */
export function tileSpecKey(spec: TileSpec): string {
    const fieldPart = spec.viz === 'trend' ? '' : spec.primaryField;
    const limitPart = spec.viz === 'trend' ? '' : (spec.limit ?? '');
    const filters = (spec.filters ?? [])
        .map((filter) => `${filter.field}=${[...filter.values].sort().join('|')}`)
        .sort()
        .join(';');
    return `${spec.viz}|${fieldPart}|${spec.metric}|${spec.bucket ?? ''}|${limitPart}|${filters}`;
}

/** Re-derives an assist chart plan from a tile spec so the shared chart renderer can draw it. */
export function chartPlanFromTileSpec(spec: TileSpec): AssistChartPlan {
    return {
        workflow: 'chart',
        title: spec.title,
        narrative: '',
        viz: spec.viz,
        primaryField: spec.primaryField,
        metric: spec.metric,
        bucket: spec.bucket,
        filters: spec.filters,
    };
}

const METRIC_VALUES = new Set<string>(ASSIST_METRICS.map((metric) => metric.value));
const VIZ_VALUES = new Set<string>(ASSIST_VIZ_KINDS);
const BUCKET_VALUES = new Set<string>(ASSIST_TIME_BUCKETS);

function isFilterArray(value: unknown): value is AnalyticsFilter[] {
    return (
        Array.isArray(value) &&
        value.every(
            (entry) =>
                typeof entry === 'object' &&
                entry !== null &&
                typeof (entry as { field: unknown }).field === 'string' &&
                (entry as { field: string }).field.length > 0 &&
                Array.isArray((entry as { values: unknown }).values) &&
                (entry as { values: unknown[] }).values.every((item) => typeof item === 'string'),
        )
    );
}

/** Whether a value is a structurally valid {@link TileSpec} with known fields/metrics/viz. */
export function isValidTileSpec(value: unknown): value is TileSpec {
    if (typeof value !== 'object' || value === null) return false;
    const spec = value as Record<string, unknown>;
    if (typeof spec.title !== 'string') return false;
    if (typeof spec.viz !== 'string' || !VIZ_VALUES.has(spec.viz)) return false;
    if (typeof spec.primaryField !== 'string' || spec.primaryField.length === 0) return false;
    if (typeof spec.metric !== 'string' || !METRIC_VALUES.has(spec.metric)) return false;
    if (spec.bucket !== undefined && (typeof spec.bucket !== 'string' || !BUCKET_VALUES.has(spec.bucket))) return false;
    if (spec.limit !== undefined && (typeof spec.limit !== 'number' || !Number.isFinite(spec.limit) || spec.limit <= 0)) return false;
    if (spec.filters !== undefined && !isFilterArray(spec.filters)) return false;
    return true;
}

/**
 * Whether a value is a valid persisted tile. Used to defensively drop entries
 * that reference fields/metrics removed from the analytics vocabulary since the
 * tile was saved, so a stale localStorage payload never crashes the gallery.
 */
export function isValidPinnedTile(value: unknown): value is PinnedTile {
    if (typeof value !== 'object' || value === null) return false;
    const tile = value as Record<string, unknown>;
    return (
        typeof tile.id === 'string' &&
        typeof tile.surfaceId === 'string' &&
        (tile.source === 'ama' || tile.source === 'add-insight') &&
        typeof tile.savedAt === 'string' &&
        isValidTileSpec(tile.spec)
    );
}
